
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";

interface AnnouncementFeedProps {
  state: string;
  lga: string;
  isAdmin: boolean;
}

interface Announcement {
  id: string;
  message: string;
  created_at: string;
}

const announcementSchema = z.object({
  message: z.string().min(3, "Announcement must be at least 3 characters")
});

const AnnouncementFeed = ({ state, lga, isAdmin }: AnnouncementFeedProps) => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  
  const form = useForm<z.infer<typeof announcementSchema>>({
    resolver: zodResolver(announcementSchema),
    defaultValues: {
      message: ""
    }
  });
  
  useEffect(() => {
    fetchAnnouncements();
  }, [state, lga]);
  
  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('community_announcements')
        .select('*')
        .eq('state', state)
        .eq('local_government', lga)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setAnnouncements(data || []);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };
  
  const onSubmit = async (values: z.infer<typeof announcementSchema>) => {
    try {
      const { data, error } = await supabase
        .from('community_announcements')
        .insert([
          {
            message: values.message,
            state: state,
            local_government: lga
          }
        ]);
      
      if (error) throw error;
      
      toast.success('Announcement posted successfully');
      form.reset();
      fetchAnnouncements();
    } catch (error) {
      console.error('Error posting announcement:', error);
      toast.error('Failed to post announcement');
    }
  };
  
  return (
    <div className="space-y-4">
      {isAdmin && (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="mb-4">
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Post Announcement</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder={`Type a message for ${lga}...`}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button 
              type="submit" 
              className="mt-2" 
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? 'Posting...' : 'Post'}
            </Button>
          </form>
        </Form>
      )}
      
      {loading ? (
        <div className="text-center py-4">Loading announcements...</div>
      ) : announcements.length === 0 ? (
        <div className="text-center py-4 text-gray-500">No announcements yet</div>
      ) : (
        <div className="space-y-3">
          {announcements.map((announcement) => (
            <div
              key={announcement.id}
              className="p-3 bg-white rounded-md border"
            >
              <p className="text-sm">{announcement.message}</p>
              <div className="text-xs text-gray-500 mt-2">
                {new Date(announcement.created_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnnouncementFeed;
